import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  deepCopy,
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import {
  AppMetaData,
  AppMetaDataItemNames,
  CoursePurchaseEntity,
  CoursePurchaseStudentEntity,
  StudentEntity
} from '../../../../meta-data/app-meta-data.service';
import { StudentGroupSelectionValue } from '../../models/student-group-selection.model';
import { StudentRepositoryService } from '../../../students/services/student-repository.service';

@DwComponent({
  key: 'course-purchase-student-group-select-form-section',
  name: 'Course Purchase Student Group Select Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.CoursePurchase,
  isGlobal: false,
})
@Component({
  selector: 'app-course-purchase-student-group-select-form-section',
  template: `
    <div class="dw-form-field">
      <div class="sg-form-label-row">
        <label ng-reflect-ng-class="label" class="label" for="course-purchase-student-group-select">
          Assign To
          <span title="Required" class="required-label">&nbsp;*</span>
          <i class="fa fa-info-circle ml-1" hidden title="undefined"></i>
        </label>
        <span class="sg-form-total">{{ studentTotalLabel }}</span>
      </div>
      <app-student-group-select
        *ngIf="!isSectionReadOnly(); else readonly"
        [(ngModel)]="selectionValue"
        [ngModelOptions]="{ standalone: true }"
        [flightSchoolId]="flightSchoolId"
        [showLabel]="false"
        [studentLabelOverrides]="studentLabelOverrides"
        inputId="course-purchase-student-group-select"
        (resolvedStudentIdsChange)="resolvedStudentIdsChanged($event)">
      </app-student-group-select>
    </div>

    <ng-template #readonly>
      <div class="dw-chip-container" *ngIf="displayItems.length; else none">
        <div class="dw-chip" *ngFor="let item of displayItems; trackBy: trackDisplayItem">
          <span class="dw-chip-label">{{ item }}</span>
        </div>
      </div>
      <ng-template #none>
        <div>None</div>
      </ng-template>
    </ng-template>
  `,
  styles: [`
    .sg-form-label-row {
      align-items: center;
      display: flex;
      justify-content: space-between;
      line-height: 1.2;
      margin-bottom: var(--card-eyebrow-gap);
    }

    .sg-form-label-row .label {
      margin-bottom: 0;
    }

    .sg-form-total {
      color: var(--text-tertiary);
      font: var(--font-semibold) var(--type-caption) var(--active-body-font);
    }

  `]
})
export class CoursePurchaseStudentGroupSelectFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  coursePurchaseStudents?: AbstractControl;
  formApi: DwMetaDataFormApi;
  selectionValue: StudentGroupSelectionValue = {
    studentIds: [],
    flightSchoolGroupIds: []
  };
  flightSchoolId: number;
  displayItems: string[] = [];
  studentTotalLabel = '0 students total';
  studentLabelOverrides: { [studentId: number]: string } = {};

  private readonly controlName = 'CoursePurchaseStudent';
  private readonly destroy$ = new Subject<void>();
  private resolvedStudentIds: number[] = [];
  private selectionHydratedFromForm = false;
  private syncingFromForm = false;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    protected dwFormStateService: DwMetaDataFormStateService,
    private adminManagerService: AdminManagerService,
    private studentRepositoryService: StudentRepositoryService,
    private route: ActivatedRoute
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.coursePurchaseStudents = this.formGroup.get(this.controlName);

    if (!this.coursePurchaseStudents) {
      console.warn(`Form control ${this.controlName} not found in form group.`);
    }

    this.runAfterFormDataLoaded(() => {
      this.syncFromForm(true);
      this.addQueryStringUserStudent();
    });

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncFromForm(false));

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    return;
  }

  protected submittedChanged(): void {
    return;
  }

  resolvedStudentIdsChanged(studentIds: number[]): void {
    this.resolvedStudentIds = this.distinct(studentIds || []);
    this.updateStudentTotalLabel(this.resolvedStudentIds.length);

    if (this.syncingFromForm) {
      return;
    }

    this.updateCoursePurchaseStudents(this.resolvedStudentIds);
  }

  trackDisplayItem(index: number, item: string): string {
    return item;
  }

  private syncFromForm(hydrateSelection: boolean): void {
    const coursePurchase = this.formApi.getFormData<CoursePurchaseEntity>();
    const aviatorUser = this.adminManagerService.getAviatorUser();
    const nextFlightSchoolId = aviatorUser?.FlightSchoolId || coursePurchase?.FlightSchoolGroup?.FlightSchoolId;

    if (this.flightSchoolId !== nextFlightSchoolId) {
      this.flightSchoolId = nextFlightSchoolId;
    }

    const activeStudents = this.getActiveCoursePurchaseStudents();
    const studentIds = activeStudents
      .map(student => student.StudentId)
      .filter((studentId): studentId is number => !!studentId);

    this.displayItems = activeStudents.map(student => student.StudentIdDisplay || `Student ${student.StudentId}`);

    if (!hydrateSelection || this.selectionHydratedFromForm) {
      this.updateStudentTotalLabel(studentIds.length);
      return;
    }

    this.syncingFromForm = true;
    this.selectionValue = {
      studentIds: this.distinct(studentIds),
      flightSchoolGroupIds: []
    };
    this.resolvedStudentIds = [...this.selectionValue.studentIds];
    this.updateStudentTotalLabel(this.resolvedStudentIds.length);
    this.selectionHydratedFromForm = true;
    this.syncingFromForm = false;
  }

  private updateCoursePurchaseStudents(studentIds: number[]): void {
    if (!this.coursePurchaseStudents) {
      console.warn(`Form control ${this.controlName} not found in form group. Student group selection is loaded but cannot update the form value.`);
      return;
    }

    const selectedStudentIds = this.distinct(studentIds || []);
    const selectedStudentIdSet = new Set(selectedStudentIds);
    const coursePurchase = this.formApi.getFormData<CoursePurchaseEntity>();
    const currentCoursePurchaseStudents = (
      coursePurchase?.CoursePurchaseStudent || this.coursePurchaseStudents.value || []
    ) as CoursePurchaseStudentEntity[];
    const allCoursePurchaseStudents = currentCoursePurchaseStudents
      .filter(coursePurchaseStudent => selectedStudentIdSet.has(coursePurchaseStudent.StudentId)
        || Number(coursePurchaseStudent.CoursePurchaseStudent || 0) > 0);

    allCoursePurchaseStudents.forEach(coursePurchaseStudent => {
      coursePurchaseStudent._isDeleted = !selectedStudentIdSet.has(coursePurchaseStudent.StudentId);
    });

    for (const studentId of selectedStudentIds) {
      let coursePurchaseStudent = allCoursePurchaseStudents.find(item => item.StudentId === studentId);

      if (!coursePurchaseStudent) {
        coursePurchaseStudent = AppMetaData.CoursePurchaseStudent.CreateEntity();
        coursePurchaseStudent.StudentId = studentId;
        allCoursePurchaseStudents.push(coursePurchaseStudent);
      }

      if (this.studentLabelOverrides[studentId] && !coursePurchaseStudent.StudentIdDisplay) {
        coursePurchaseStudent.StudentIdDisplay = this.studentLabelOverrides[studentId];
      }

      coursePurchaseStudent._isDeleted = false;
    }

    const patchData: Partial<CoursePurchaseEntity> = deepCopy({ CoursePurchaseStudent: allCoursePurchaseStudents });

    this.formApi.patchFormData(patchData);
    this.formApi.patchContextData('data', patchData);
    this.displayItems = allCoursePurchaseStudents
      .filter(student => student._isDeleted !== true)
      .map(student => student.StudentIdDisplay || `Student ${student.StudentId}`);
    this.updateStudentTotalLabel(selectedStudentIds.length);
  }

  private addQueryStringUserStudent(): void {
    const queryUserId = Number(
      this.route.snapshot.queryParamMap.get('UserId') ||
      this.route.snapshot.queryParamMap.get('userId')
    );

    if (!Number.isInteger(queryUserId) || queryUserId <= 0 || !this.flightSchoolId) {
      return;
    }

    this.studentRepositoryService.getStudentFromUserIdForSchool(queryUserId, this.flightSchoolId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(student => {
        if (!student?.StudentId) {
          return;
        }

        const studentId = Number(student.StudentId);

        if (this.resolvedStudentIds.indexOf(studentId) >= 0) {
          return;
        }

        const studentDisplay = this.resolveStudentDisplay(student, studentId);

        this.studentLabelOverrides = {
          ...this.studentLabelOverrides,
          [studentId]: studentDisplay
        };
        this.selectionValue = {
          studentIds: this.distinct([...this.selectionValue.studentIds, studentId]),
          flightSchoolGroupIds: [...this.selectionValue.flightSchoolGroupIds]
        };
        this.resolvedStudentIds = this.distinct([...this.resolvedStudentIds, studentId]);
        this.updateCoursePurchaseStudents(this.resolvedStudentIds);
      });
  }

  private resolveStudentDisplay(student: StudentEntity, studentId: number): string {
    const firstName = (student as any).DwUserFirstName || student.DwUser?.FirstName || '';
    const lastName = (student as any).DwUserLastName || student.DwUser?.LastName || '';

    return `${firstName} ${lastName}`.trim() || student.UserIdDisplay || `Student ${studentId}`;
  }

  private getActiveCoursePurchaseStudents(): CoursePurchaseStudentEntity[] {
    return (((this.coursePurchaseStudents?.value || []) as CoursePurchaseStudentEntity[])
      .filter(student => student._isDeleted !== true));
  }

  private distinct(values: number[]): number[] {
    return values.filter((value, index, source) => source.indexOf(value) === index);
  }

  private updateStudentTotalLabel(count: number): void {
    this.studentTotalLabel = `${count} ${count === 1 ? 'student' : 'students'} total`;
  }
}
